/**
 * Firebase Auth helpers
 * Authentication utilities including claims retrieval
 */
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  getIdTokenResult,
  onIdTokenChanged,
} from 'firebase/auth'
import { getFirebaseAuth } from './firebase.js'
import logger from '../services/logger.js'

const log = logger.createScoped('FirebaseAuth')

/**
 * Valid role values for custom claims
 */
const VALID_ROLES = ['admin', 'client', 'agent']

/**
 * Sign in with email and password
 * @param {string} email
 * @param {string} password
 * @returns {Promise} User credential
 */
export async function signIn(email, password) {
  const auth = getFirebaseAuth()
  return signInWithEmailAndPassword(auth, email, password)
}

/**
 * Create a new user account
 * @param {string} email
 * @param {string} password
 * @returns {Promise} User credential
 */
export async function signUp(email, password) {
  const auth = getFirebaseAuth()
  return createUserWithEmailAndPassword(auth, email, password)
}

/**
 * Sign out the current user
 * @returns {Promise}
 */
export async function logout() {
  const auth = getFirebaseAuth()
  return signOut(auth)
}

/**
 * Subscribe to auth state changes
 * @param {Function} callback - Called with user or null
 * @returns {Function} Unsubscribe function
 */
export function subscribeToAuthState(callback) {
  const auth = getFirebaseAuth()
  return onAuthStateChanged(auth, callback)
}

/**
 * Get current user
 * @returns {Object|null} Current user or null
 */
export function getCurrentUser() {
  const auth = getFirebaseAuth()
  return auth.currentUser
}

/**
 * Get role claim from Firebase ID token
 * Returns validated role or null if missing/invalid
 * @param {Object} firebaseUser - Firebase user object
 * @returns {Promise<string|null>} Role claim or null
 */
export async function getRoleClaim(firebaseUser) {
  if (!firebaseUser) {
    return null
  }

  try {
    const tokenResult = await getIdTokenResult(firebaseUser)
    const claimRole = tokenResult?.claims?.role

    // Validate claim is a string and one of the valid roles
    if (typeof claimRole === 'string' && VALID_ROLES.includes(claimRole)) {
      return claimRole
    }

    // Log warning if claim exists but is invalid
    if (claimRole !== undefined && claimRole !== null) {
      log.warn('Invalid role claim value', { claimRole, validRoles: VALID_ROLES })
    }

    return null
  } catch (err) {
    log.error('Failed to get role claim from token', err)
    return null
  }
}

/**
 * Subscribe to ID token changes
 * Fires when token is refreshed (e.g., claims updated server-side)
 * @param {Function} callback - Called with user or null on token change
 * @returns {Function} Unsubscribe function
 */
export function subscribeToTokenChanges(callback) {
  const auth = getFirebaseAuth()
  return onIdTokenChanged(auth, callback)
}
